import { Widget, Common, color } from '@stockbit/ui';
import { Skeleton as BaseSkeleton } from 'antd';
import styled from 'styled-components';

const { Flex, Text, DatePicker: DatePickerDefault } = Common;
const { Table: TableDefault } = Widget;

const cellType = {
  up: color.primary,
  down: color.sunsetOrange,
  neutral: color.suvaGrey,
  default: color.highempahis,
  local: color.purpleHeart,
  bumn: color.green,
  foreign: color.venetianRed,
};

const actionColor = {
  0: '#333333',
  1: '#00ab6b',
  2: '#ee4a49',
};

const TableInitWrapper = styled.div`
  overflow: auto;

  & table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    table-layout: fixed;

    th,
    td {
      text-align: center;
      font-size: 12px;
      line-height: 30px;
      padding: 0;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    th {
      font-family: var(--bold-font);
      position: sticky;
      top: 0;
      background-color: #fff;
      border-bottom: 1px solid ${color.gainsboro};
    }

    td {
      transition: background-color 0.3s;
    }

    td.company-code {
      font-family: var(--bold-font);
    }

    tr:hover {
      td {
        background-color: #f5f6f7;
      }
    }
  }
`;

const Table = styled(TableDefault)`
  white-space: nowrap;
  text-overflow: ellipsis !important;

  & table th p {
    font-size: 12px;
    font-family: var(--bold-font);
    text-align: center;
  }

  & table td {
    background: transparent;
  }

  & table td p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ant-spin-nested-loading > div > .ant-spin {
    display: flex;
    align-items: center;
    justify-content: center;

    .ant-spin-dot {
      position: static;
      margin: 0;
    }
  }

  thead.ant-table-thead {
    th.ant-table-cell {
      background-color: #fff;
      padding-bottom: 10px;
      border-bottom: 1px solid ${color.gainsboro};
      text-align: center;

      .ant-table-column-sorters {
        padding: unset;
      }
    }
  }
`;

const HeaderContainer = styled(Flex).attrs({
  align: 'center',
  justify: 'space-between',
  px: '10px',
})`
  position: relative;
  background: ${color.aliceBlue};
  height: 40px;
  cursor: pointer;
  border-bottom: 1px solid ${color.gainsboro};
`;

const Td = styled(Text).attrs((props) => ({
  height: '30px',
  size: '12px',
  type: props.type,
  isColored: props.isColored,
}))`
  text-align: center;

  color: ${({ isColored, type }) => {
    if (!isColored) return actionColor[0];
    return actionColor[type] || actionColor[0];
  }};
`;

const TdSymbol = styled(Text).attrs(() => ({
  height: '30px',
  size: '12px',
  weight: 'bold',
}))`
  text-align: center;
  color: #00ab6b;
`;

const TdAction = styled(Text).attrs((props) => ({
  height: '30px',
  size: '12px',
  type: props.type,
  isColored: props.isColored,
}))`
  text-align: center;

  color: ${({ isColored, type }) =>
    !isColored ? `${color.nightRider}` : type == 1 ? '#00ab6b' : '#ee4a49'};
`;

const TdPrice = styled(Text).attrs((props) => ({
  height: '30px',
  size: '12px',
  type: props.type,
  isColored: props.isColored,
}))`
  text-align: center;

  color: ${({ isColored, change }) =>
    !isColored
      ? `${color.nightRider}`
      : change == 0
      ? `${color.suvaGrey}`
      : change > 0
      ? '#00ab6b'
      : '#ee4a49'};
`;

const Th = styled(Td).attrs({
  size: '8px',
})``;

const DatePicker = styled(DatePickerDefault)`
  flex: 1;
  border-top: none !important;
  height: 32px;
  :nth-child(1) {
    border-right: none !important;
  }
  :hover,
  :focus {
    border-top: none !important;
    :nth-child(1) {
      border-right: none !important;
    }
  }
`;

const ReloadButton = styled(Flex).attrs({
  align: 'center',
  px: '20px',
  mt: '20px',
})`
  background: ${color.primary};
  height: 44px;
  border-radius: 22px;
  cursor: pointer;

  p {
    color: ${color.light};
    margin-left: 5px;
    font-weight: 700;
    font-family: var(--bold-font);
  }
`;

const Box = styled.div`
  overflow-y: auto;
`;

const SearchWrapper = styled.div`
  padding: 10px;
`;

const Skeleton = styled(BaseSkeleton).attrs({
  active: true,
  avatar: false,
  loading: true,
  title: false,
  round: true,
})`
  padding: 20px;
  .ant-skeleton-content > ul {
    margin: 0 !important;
  }
  .ant-skeleton-content .ant-skeleton-title,
  .ant-skeleton-content .ant-skeleton-paragraph > li {
    height: 16px !important;
    margin: 0 0 14px !important;
    border-radius: 4px !important;
  }

  .ant-skeleton-content .ant-skeleton-paragraph > li:last-child {
    margin: 0 !important;
  }
`;

export const RunningTradeEmptyWrapper = styled(Flex)`
  min-height: 300px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const RunningTradeEmptyText = styled(Text)`
  margin-top: 8px;
  color: #858585;
  font-size: 12px;
`;

export const ThSortWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`;

export const ThSortIconWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  font-size: 11px;
  margin-left: 8px;
`;

export const ThSortIconContent = styled.span<{ active: boolean }>`
  display: inline-flex;
  color: ${({ active }) => (active ? color.primary : '#bfbfbf')};

  & + & {
    margin-top: -0.3em;
  }
`;

const brokerCodeColor: Record<RunningTradeBrokerType, string> = {
  BROKER_TYPE_FOREIGN: 'red',
  BROKER_TYPE_GOVERNMENT: 'green',
  BROKER_TYPE_LOCAL: 'purple',
  BROKER_TYPE_UNSPECIFIED: '#000',
};
interface ITdBroker {
  type: RunningTradeBrokerType;
}

const TdBroker = styled.td<ITdBroker>`
  color: ${({ type }) => brokerCodeColor[type]};
`;

export {
  TableInitWrapper,
  Table,
  HeaderContainer,
  Td,
  TdSymbol,
  TdPrice,
  TdAction,
  Th,
  DatePicker,
  Box,
  SearchWrapper,
  ReloadButton,
  Skeleton,
  TdBroker,
};
